import {
  ExecutionFrequencyType,
  IntegrationType,
  UpdateWorkflowRequest,
  WorkflowsService,
} from '@/api';
import CronScheduler from '@/components/cronScheduler';
import type { ITimezone } from '@/components/timeZoneSelector';
import TimeZoneSelector from '@/components/timeZoneSelector';
import { useStore } from '@nanostores/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Info, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';


interface Props {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setNodes: any;
  selectedNode: any;
}

const WORKFLOW_TYPE_MAP = {
  "BulkRun": "BulkRun",
  "SingleRun": "SingleRun",
} as const;

type DataIngestionNodeData = {
  dataSource: IntegrationType;
};

export default function WorkflowGlobalConfigurationDrawer({
  open,
  setOpen,
  setNodes,
  selectedNode,
}: Props) {
  const wF = useStore($workflow);
  const queryClient = useQueryClient();
  const [frequency, setFrequency] = useState<string>(
    wF?.type === WORKFLOW_TYPE_MAP.BulkRun 
      ? 'manual'  // Force manual for bulk workflows
      : (wF?.executionFrequencyType?.toLowerCase() || '')
  );
  const [hour, setHour] = useState<string>(
    wF?.executionHour?.toString() || '0',
  );
  const [minute, setMinute] = useState<string>(
    wF?.executionMinute?.toString() || '0',
  );
  const [daysOfWeek, setDaysOfWeek] = useState<string[]>(
    wF?.executionDaysOfWeek || [],
  );
  const [daysOfMonth, setDaysOfMonth] = useState<number[]>(
    wF?.executionDaysOfMonth || [],
  );
  const [selectedTimezone, setSelectedTimezone] = useState<ITimezone>(
    wF?.timeZone || '',
  );
  useEffect(() => {
    if (wF) {
      setSelectedTimezone(wF.timeZone || '');
      // Set frequency to manual for bulk workflows, otherwise use workflow's frequency
      setFrequency(wF.type === WORKFLOW_TYPE_MAP.BulkRun 
        ? 'manual' 
        : (wF.executionFrequencyType?.toLowerCase() || ''));
      setHour(wF.executionHour?.toString() || '0');
      setMinute(wF.executionMinute?.toString() || '0');
      setDaysOfWeek(wF.executionDaysOfWeek || []);
      setDaysOfMonth(wF.executionDaysOfMonth || []);
    }
  }, [wF]);

  // mutation to update a node
  const updateWorkflow = useMutation({
    mutationFn: async (payload: UpdateWorkflowRequest) => {
      // call the api to create a new workspace
      if (wF) {
        const res = await WorkflowsService.updateWorkflow(wF.id, payload);
        return res;
      }
    },
    onSuccess: async data => {
      await queryClient.invalidateQueries({
        queryKey: ['getWorkflow', wF?.id],
      });
    },
    onError: error => {
      console.error(error);

      // show a toast message
      toast('An error occurred :: Unable to add node.', {
        description: error.message,
        duration: 5000,
      });
    },
  });

  return (
    <div
      className={`absolute right-6 top-20 min-h-[160px] min-w-[400px] rounded-lg bg-white p-4 pl-6 pr-6 ${!open && 'hidden'} z-10 gap-2.5`}
    >
      <div className="flex justify-between">
        <p className="text-sm font-semibold tracking-tight">Settings </p>
        <button type="button" onClick={() => setOpen(false)}>
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="mt-4">
        <p className="text-sm font-semibold [&:not(:first-child)]:mt-6">
          Timezone
        </p>
        <div className="mb-3 flex items-center">
          <Info className="mr-1 h-3 w-3 text-muted-foreground" />
          <p className="tracking-light text-xs text-muted-foreground">
            Indicates the timezone in which the workflow will run.
          </p>
        </div>
        <TimeZoneSelector
          selectedTimezone={selectedTimezone}
          onChange={(timezone: ITimezone) => setSelectedTimezone(timezone)}
          label="Select the timezone for workflow execution"
        />
      </div>

      <div className="mt-4 w-[100%]">
        {wF?.type === WORKFLOW_TYPE_MAP.BulkRun ? (
          <>
            <div className="text-sm text-muted-foreground mb-4">
              Bulk workflows can only be executed manually.
            </div>
            <div className="flex justify-end mt-6">
              <Button
                variant="outline"
                onClick={() => {
                  if (wF) {
                    if (!selectedTimezone) {
                      toast('Please select timezone!', {
                        description: 'Timezone is required!',
                        duration: 5000,
                      });
                      return;
                    }
                    updateWorkflow.mutateAsync({
                      executionFrequencyType: 'Manual',
                      timeZone: typeof selectedTimezone === 'string'
                        ? selectedTimezone
                        : selectedTimezone.value,
                    });
                  }
                }}
                disabled={updateWorkflow.isPending}
              >
                {updateWorkflow.isPending ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </>
        ) : (
          <CronScheduler
            frequency={frequency}
            hour={hour}
            minute={minute}
            daysOfWeek={daysOfWeek}
            daysOfMonth={daysOfMonth}
            isLoading={updateWorkflow.isPending}
            onSave={frequencyObject => {
              if (wF) {
                if (
                  !selectedTimezone ||
                  !frequencyObject ||
                  !frequencyObject.type
                ) {
                  toast('Please select all the required configurations!', {
                    description:
                      'Timezone, workflow frequency and execution time is required!',
                    duration: 5000,
                  });
                  return;
                }
                updateWorkflow.mutateAsync({
                  executionFrequencyType:
                    frequencyObject.type as ExecutionFrequencyType,
                  executionHour: parseInt(frequencyObject.hour),
                  executionMinute: parseInt(frequencyObject.minute),
                  executionDaysOfWeek: frequencyObject.days_of_week,
                  executionDaysOfMonth: frequencyObject.days_of_month,
                  timeZone:
                    typeof selectedTimezone === 'string'
                      ? selectedTimezone
                      : selectedTimezone.value,
                });
              }
            }}
          />
        )}
      </div>
    </div>
  );
}
